import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import {
  getSingleFilteredOption,
  handleSingleOptionEnterToggle,
} from 'feature/loading-management/utils/autocompleteEnterToggle';
import {
  useGetAuthSeqFilterLocationsListQuery,
  useGetAuthSeqFilterProductsListQuery,
} from '../authorizationSequencingEndpoints';

const STATUS_OPTIONS = ['All', 'Loadable', 'Not Loadable'];

const multiSelectCompactSx = {
  '& .MuiAutocomplete-inputRoot': {
    alignItems: 'center',
    flexWrap: 'nowrap',
    minHeight: 48,
  },
};

const renderCollapsedTags = selectedOptions => {
  if (!selectedOptions.length) return null;

  const firstLabel = selectedOptions[0]?.label ?? selectedOptions[0]?.value ?? '';

  return (
    <>
      <span
        style={{
          display: 'inline-block',
          marginLeft: 5,
          marginRight: 8,
          maxWidth: 200,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {firstLabel}
      </span>
      {selectedOptions.length > 1 && (
        <Chip label={`+${selectedOptions.length - 1}`} size="small" variant="autocomplete" />
      )}
    </>
  );
};

const iconStyles = { position: 'absolute', right: 12, top: 14 };

const renderInputWithSpinner = (label, loading, optionsLength, error, helperText) => params =>
  (
    <TextField
      {...params}
      error={error}
      helperText={helperText}
      label={label}
      slotProps={{
        input: {
          ...params.InputProps,
          endAdornment: (
            <>
              {loading ? (
                <CircularProgress size={20} sx={{ color: 'secondary.main' }} />
              ) : !loading && !optionsLength ? (
                <Tooltip title="No options available">
                  <InfoRoundedIcon sx={{ color: 'graphic.main', ...iconStyles }} />
                </Tooltip>
              ) : null}
              {!!optionsLength && params.InputProps.endAdornment}
            </>
          ),
        },
      }}
    />
  );

const getOptionLabel = option => option?.label || '';
const isOptionEqual = (option, value) => option?.value === value?.value;

const AuthSequencingFilters = ({
  affiliateFilter,
  affiliateOptions,
  locationFilter,
  productFilter,
  setAffiliateFilter,
  setLocationFilter,
  setProductFilter,
  setStatusFilter,
  statusFilter,
}) => {
  const [productInputValue, setProductInputValue] = useState('');
  const [locationInputValue, setLocationInputValue] = useState('');
  const [productFocused, setProductFocused] = useState(false);

  const showAffiliateFilter = affiliateOptions.length > 1;

  // Build params for products and locations queries
  const supplierParams = useMemo(
    () => ({
      'Supplier.CompanyCustomerIdName': affiliateFilter?.customerIdName,
      'Supplier.DataCompanyId': affiliateFilter?.dataCompanyId,
    }),
    [affiliateFilter]
  );

  const hasSupplierParams =
    !!supplierParams['Supplier.CompanyCustomerIdName'] &&
    !!supplierParams['Supplier.DataCompanyId'];

  const { data: productsData = [], isLoading: isProductsLoading } =
    useGetAuthSeqFilterProductsListQuery(supplierParams, {
      skip: !hasSupplierParams,
    });

  const { data: locationsData = [], isLoading: isLocationsLoading } =
    useGetAuthSeqFilterLocationsListQuery(supplierParams, {
      skip: !hasSupplierParams,
    });

  const productOptions = useMemo(
    () =>
      (Array.isArray(productsData) ? productsData : [])
        .map(item => ({
          label: item.nameLong || item.productNameLong || '',
          value: item.productId || item.id,
        }))
        .filter(o => o.label && o.value != null),
    [productsData]
  );

  const locationOptions = useMemo(
    () =>
      (Array.isArray(locationsData) ? locationsData : [])
        .map(item => ({
          label: item.nameLong || item.locationNameLong || '',
          value: item.locationId || item.id,
        }))
        .filter(o => o.label && o.value != null),
    [locationsData]
  );

  const productSingleFilteredOption = useMemo(
    () => getSingleFilteredOption(productOptions, productInputValue, getOptionLabel),
    [productInputValue, productOptions]
  );

  const locationSingleFilteredOption = useMemo(
    () => getSingleFilteredOption(locationOptions, locationInputValue, getOptionLabel),
    [locationInputValue, locationOptions]
  );

  // Validation
  const showProductError =
    productFilter.length === 0 && productOptions.length > 0 && productFocused;

  return (
    <>
      {/* Affiliate */}
      {showAffiliateFilter && (
        <>
          <Autocomplete
            autoHighlight
            disableClearable
            disablePortal
            getOptionLabel={option => option?.customerNameLong ?? ''}
            id="auth-seq-affiliate-filter"
            isOptionEqualToValue={(option, value) =>
              option?.customerIdName === value?.customerIdName &&
              option?.dataCompanyId === value?.dataCompanyId
            }
            onChange={(event, newValue) => setAffiliateFilter(newValue)}
            options={affiliateOptions}
            renderInput={params => <TextField {...params} label="Affiliate" />}
            value={affiliateFilter || affiliateOptions[0] || null}
          />
          <Divider />
        </>
      )}
      {/* Product — multi-select */}
      <Autocomplete
        autoHighlight
        disableCloseOnSelect
        disablePortal
        getOptionLabel={getOptionLabel}
        id="auth-seq-product-filter"
        inputValue={productInputValue}
        isOptionEqualToValue={isOptionEqual}
        multiple
        onBlur={() => setProductFocused(true)}
        onChange={(event, newValue) => setProductFilter(newValue)}
        onInputChange={(event, newInputValue) => setProductInputValue(newInputValue)}
        onKeyDown={event =>
          handleSingleOptionEnterToggle(
            event,
            productSingleFilteredOption,
            productFilter,
            setProductFilter,
            isOptionEqual
          )
        }
        openOnFocus
        options={productOptions}
        renderInput={renderInputWithSpinner(
          'Products',
          isProductsLoading,
          productOptions.length,
          showProductError,
          showProductError ? 'At least 1 product must be selected' : undefined
        )}
        renderOption={(props, option) => (
          // eslint-disable-next-line react/jsx-props-no-spreading
          <li {...props} key={option.value}>
            {option.label}
          </li>
        )}
        renderTags={renderCollapsedTags}
        sx={multiSelectCompactSx}
        value={productFilter}
      />
      {/* Location — multi-select */}
      <Autocomplete
        autoHighlight
        disableCloseOnSelect
        disablePortal
        getOptionLabel={getOptionLabel}
        id="auth-seq-location-filter"
        inputValue={locationInputValue}
        isOptionEqualToValue={isOptionEqual}
        multiple
        onChange={(event, newValue) => setLocationFilter(newValue)}
        onInputChange={(event, newInputValue) => setLocationInputValue(newInputValue)}
        onKeyDown={event =>
          handleSingleOptionEnterToggle(
            event,
            locationSingleFilteredOption,
            locationFilter,
            setLocationFilter,
            isOptionEqual
          )
        }
        openOnFocus
        options={locationOptions}
        renderInput={renderInputWithSpinner(
          'Locations',
          isLocationsLoading,
          locationOptions.length
        )}
        renderOption={(props, option) => (
          // eslint-disable-next-line react/jsx-props-no-spreading
          <li {...props} key={option.value}>
            {option.label}
          </li>
        )}
        renderTags={renderCollapsedTags}
        sx={multiSelectCompactSx}
        value={locationFilter}
      />
      {/* Status — single-select, non-clearable */}
      <Autocomplete
        autoHighlight
        disableClearable
        disablePortal
        id="auth-seq-status-filter"
        onChange={(event, newValue) => setStatusFilter(newValue)}
        options={STATUS_OPTIONS}
        renderInput={params => <TextField {...params} label="Status" />}
        value={statusFilter || 'All'}
      />
    </>
  );
};

AuthSequencingFilters.propTypes = {
  affiliateFilter: PropTypes.shape({
    customerIdName: PropTypes.string,
    customerNameLong: PropTypes.string,
    dataCompanyId: PropTypes.number,
  }),
  affiliateOptions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  locationFilter: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  productFilter: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  setAffiliateFilter: PropTypes.func.isRequired,
  setLocationFilter: PropTypes.func.isRequired,
  setProductFilter: PropTypes.func.isRequired,
  setStatusFilter: PropTypes.func.isRequired,
  statusFilter: PropTypes.string,
};

AuthSequencingFilters.defaultProps = {
  affiliateFilter: null,
  statusFilter: 'All',
};

export default AuthSequencingFilters;
